import React, { useEffect, lazy, Suspense } from 'react';
import { IonApp, IonRouterOutlet, IonSplitPane, setupIonicReact } from '@ionic/react';
import { IonReactRouter } from '@ionic/react-router';
import { Redirect, Route } from 'react-router-dom';

// App state
import ReactOneSignal from 'react-onesignal';
import { initFontAwesome } from './utils/fontawesome';
import { connect } from './data/connect';
import { AppContextProvider } from './data/AppContext';
import { loadAppData } from './data/app.actions';

// Eagerly loaded components (needed immediately)
import Seo from './components/Seo';
import Menu from './components/Menu';
import Loading from './components/Loading';
import ErrorBoundary from './components/ErrorBoundary';

// Global styling - Ionic CSS must load first, then custom theme overrides
import 'bootstrap/dist/css/bootstrap.min.css';
import '@ionic/react/css/core.css';
import '@ionic/react/css/normalize.css';
import '@ionic/react/css/structure.css';
import '@ionic/react/css/typography.css';
import '@ionic/react/css/padding.css';
import '@ionic/react/css/float-elements.css';
import '@ionic/react/css/text-alignment.css';
import '@ionic/react/css/text-transformation.css';
import '@ionic/react/css/flex-utils.css';
import '@ionic/react/css/display.css';
// Custom theme variables - loaded after Ionic CSS to override defaults
import './theme/variables.css';

// Lazy loaded pages (code splitting)
const Home = lazy(() => import('./pages/Home'));
const Explore = lazy(() => import('./pages/Projects'));
const Resources = lazy(() => import('./pages/Resources'));
const Contact = lazy(() => import('./pages/Contact'));

// Ionic configuration setup - Set mode to always iOS theme - Enable status bar tap scroll to top - Disable swipe back gesture
setupIonicReact({
  mode: 'ios',
  swipeBackEnabled: false,
});

// Initialize FontAwesome with only the icons we use (tree-shaking optimization)
initFontAwesome();

// OneSignal configuration options
const options = {
  safari_web_id: 'web.amiprojects.io',
  allowLocalhostAsSecureOrigin: false,
  requiresUserPrivacyConsent: false,
  persistNotification: false,
  autoResubscribe: true,
  autoRegister: false,
  notifyButton: {
    enable: false,
  },
};

// Main App Component
const App = () => {
  return (
    <ErrorBoundary>
      <AppContextProvider>
        <AppConnected />
      </AppContextProvider>
    </ErrorBoundary>
  );
};
export default App;

// App Consumert Component
const AppConsumer = ({ loadAppData, loading }) => {
  useEffect(() => {
    if (process.env.REACT_APP_ONESIGNAL_APP_ID) {
      ReactOneSignal.initialize(process.env.REACT_APP_ONESIGNAL_APP_ID, options);
    }
    loadAppData(); // Fetch projects and alerts from ArcGIS REST endpoint then store returned data in application state
  }, []);
  // If app is fetching data - render loading screen
  if (loading) {
    return <Loading />;
  }
  // If app has finished fetching data - render IonApp
  return (
    <IonApp>
      {/* Skip navigation link for keyboard users - WCAG 2.4.1 */}
      <a href="#main-content" className="skip-link">
        Skip to main content
      </a>
      <IonReactRouter>
        <Seo /> {/* Update meta tags using Helmet and log page views in Google Analtics on route changes */}
        <IonSplitPane contentId="main" when="xl">
          <Menu />
          <IonRouterOutlet id="main" aria-label="Main content" role="main">
            <Route
              exact
              path="/"
              render={() => (
                <Suspense fallback={<Loading />}>
                  <Home />
                </Suspense>
              )}
            />
            <Route
              path="/projects"
              render={() => (
                <Suspense fallback={<Loading />}>
                  <Explore />
                </Suspense>
              )}
            />
            <Route
              exact
              path="/resources"
              render={() => (
                <Suspense fallback={<Loading />}>
                  <Resources />
                </Suspense>
              )}
            />
            <Route
              exact
              path="/contact"
              render={() => (
                <Suspense fallback={<Loading />}>
                  <Contact />
                </Suspense>
              )}
            />
            <Redirect to="/" />
          </IonRouterOutlet>
        </IonSplitPane>
      </IonReactRouter>
    </IonApp>
  );
};

const AppConnected = connect({
  mapStateToProps: (state) => ({
    loading: state.data.loading,
  }),
  mapDispatchToProps: {
    loadAppData,
  },
  component: AppConsumer,
});
